import type { CSSProperties, ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LuArrowRight,
  LuCalendarCheck,
  LuCalendarDays,
  LuShieldCheck,
  LuUndo2,
  LuUsers,
} from 'react-icons/lu';

import { ApiError } from '@mds/shared/lib/api';
import { ImageGallery } from '@mds/shared/ui/image/image-gallery';
import { Button } from '@mds/shared/ui/shadcn/button';

import IconCard from '@/components/card/icon-card';
import { MediaCardRow } from '@/components/card/media-card';
import { PACKAGE_CARD_WIDTH, type PackageCardProps } from '@/components/card/package-card';
import PackageList from '@/components/card/package-list';
import SpaceCard from '@/components/card/space-card';
import PageHeader from '@/components/heading/page-header';
import RuledHeader from '@/components/heading/ruled-header';
import SectionHeader from '@/components/heading/section-header';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { spaceGroupOf } from '@/features/spaces/space-types';
import { spaceQueries } from '@/features/spaces/spaces.queries';
import { formatMoney, formatPackageUnit } from '@/lib/format';
import NotFoundPage from './not-found';

/** See the note in home.tsx — this wants to be a `<Section>` component. */
const band = 'mx-auto max-w-page px-gutter pt-section';

/** How many other spaces the closing band offers — one row of the design. */
const ALSO_AVAILABLE = 3;

/**
 * The booking rules, the same for every space: a short form of the booking and
 * use terms (`@/data/booking-policy`). Written here rather than read from the
 * API's `bookingRules`, which the page ignores, as it does `amenities`.
 */
const RULES: { title: string; text: string; icon: ReactNode }[] = [
  {
    title: 'Booking and payment',
    text: 'Your booking is confirmed only after MDS approval and receipt of the required payment.',
    icon: <LuCalendarCheck />,
  },
  {
    title: 'Cancellation',
    text: '7+ days: full refund; 3–6 days: 50% refund; less than 3 days or no-show: no refund.',
    icon: <LuUndo2 />,
  },
  {
    title: 'Use and conduct',
    text: 'Keep to your booked space and time, respect the community, and follow safety instructions.',
    icon: <LuShieldCheck />,
  },
  {
    title: 'Customer responsibility',
    text: 'You are responsible for your guests and any damage, extra cleaning or overtime charges.',
    icon: <LuUsers />,
  },
];

export default function SpaceDetailPage() {
  const { slug = '' } = useParams();
  const detail = useQuery(spaceQueries.detail(slug));
  // One space of each other kind, so the band offers a different thing to hire
  // rather than the second of two rooms. Read from the list the spaces page and
  // the home page already cached, so this costs no request of its own there.
  const others = useQuery({
    ...spaceQueries.list(),
    select: (spaces) => {
      const current = spaces.find((s) => s.slug === slug);
      const currentGroup = current && spaceGroupOf(current.type);
      return spaces
        .filter((s) => spaceGroupOf(s.type) !== currentGroup)
        .filter(
          (s, i, all) => all.findIndex((o) => spaceGroupOf(o.type) === spaceGroupOf(s.type)) === i,
        )
        .slice(0, ALSO_AVAILABLE);
    },
  });

  // A slug nobody has is a wrong URL, not an empty state — the 404 is the
  // honest answer and it keeps the wrong address out of the index.
  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return <NotFoundPage />;
  }

  if (detail.isPending || detail.isError) {
    return (
      <section className={band}>
        {detail.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load this space"
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </section>
    );
  }

  const space = detail.data;
  const about = space.longDescription.split(/\n\s*\n/).filter(Boolean);
  // Exactly what the API sends. It already starts with the cover, so the
  // cover is not added separately: that would show it twice.
  const gallery = space.gallery;
  // One mapping for both layouts — the phone's rows and the desktop's pricing cards.
  const packages = space.packages.map((pkg) => ({
    key: pkg.id,
    props: {
      name: pkg.name,
      price: pkg.quoteOnly ? 'By quote' : formatMoney(pkg.amountMinor),
      unit: pkg.quoteOnly
        ? ''
        : formatPackageUnit({ unit: pkg.unit, unitLabel: pkg.unitLabel ?? undefined }),
      tag: pkg.note,
      perks: pkg.benefits.map((benefit) => benefit.title),
    } satisfies PackageCardProps,
  }));

  return (
    <>
      <section className={band}>
        <PageHeader
          back={{ label: 'All spaces', to: '/spaces' }}
          title={space.name}
          description={space.shortDescription}
        />
      </section>

      {/* The pictures and the words about them, side by side from `lg`, so the
          room the capped gallery leaves is read rather than empty. Stacked
          below that, where a column beside the gallery would squeeze both. */}
      <section className="mx-auto flex max-w-page flex-col gap-block px-gutter pt-block lg:flex-row lg:items-start">
        {/* `feature-right` is the design's main-frame-plus-thumbnail-rail, and
            the shared gallery already opens the full-screen viewer from it. */}
        {gallery.length > 0 && (
          <ImageGallery
            label={space.name}
            layout="feature-right"
            images={gallery.map((src) => ({ src, alt: space.name }))}
            className="lg:w-3/5 lg:shrink-0"
          />
        )}

        <div className="min-w-0 flex-1">
          <SectionHeader as="h2" title="About this space" />
          <div className="mt-5 flex flex-col gap-5">
            {about.map((paragraph) => (
              <p key={paragraph} className="text-body-lg text-pretty text-ink-secondary">
                {paragraph}
              </p>
            ))}
            {/* The café hall serves food and drink: its menu has a page of its
                own, linked from the last line of the text. */}
            {space.type === 'cafe' && (
              <p className="text-body-lg text-pretty text-ink-secondary">
                Coffee, fresh bakes and light bites are served all day.{' '}
                <Link
                  to="/cafe/menu"
                  className="rounded-sm text-brand-text underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                  See the café menu
                </Link>
                .
              </p>
            )}
          </div>
          {/* Proposal spaces are not booked off a board: the visitor describes
              the plan and MDS replies, so this is their only way in. On a line
              of its own, under the text. */}
          {space.bookingMode === 'proposal' && (
            <div className="mt-block">
              <Button asChild size="lg">
                <Link to={`/spaces/${space.slug}/proposal`}>Send a proposal</Link>
              </Button>
            </div>
          )}
          {/* Any room configured for direct requests is booked off its own
              availability board, on a page of its own. */}
          {space.type !== 'desk' && space.bookingMode === 'direct_request' && (
            <div className="mt-block">
              <Button asChild size="lg">
                <Link to={`/spaces/${space.slug}/book`}>
                  <LuCalendarDays aria-hidden="true" />
                  Select a date
                </Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Rooms and proposal spaces have no packages; the section goes with them. */}
      {space.packages.length > 0 && (
        <section className={band}>
          <RuledHeader title="Packages" />
          {/* No `cta` / `action` per package yet: the booking flow is not on
              the API yet. */}
          <PackageList packages={packages} />
          {/* Desks go to the same booking page as the home page's "Book a desk",
              opened on this floor: a desk space's slug is its floor's map key. */}
          {space.type === 'desk' && (
            <div className="mt-block flex justify-center">
              {/* Full width under the phone's rows; one card wide under the
                  cards. The row it sits in is the band's full width, so the
                  `100%` in the card width means the same as it does above. */}
              <div
                className="w-full lg:w-(--card-width)"
                style={{ '--card-width': PACKAGE_CARD_WIDTH } as CSSProperties}
              >
                <Button asChild size="lg" className="w-full">
                  <Link
                    to={`/book/daily-desk?floor=${space.slug}`}
                    state={{ from: `/spaces/${space.slug}` }}
                  >
                    Book your desk
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className={band}>
        <RuledHeader title="Booking rules" />
        <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
          {RULES.map((rule) => (
            <IconCard
              key={rule.title}
              icon={rule.icon}
              eyebrow={rule.title}
              description={rule.text}
              accent="brand"
              quiet
              // 13px, a notch under `quiet`'s 14px: four of these side by side
              // read as fine print, not as paragraphs.
              className="[&_p]:text-sm"
            />
          ))}
        </div>
      </section>

      <section className={band}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeader as="h2" title="Also available" />
          <Link
            to="/spaces"
            className="group inline-flex items-center gap-2 rounded-sm font-mono text-2xs text-brand-text uppercase underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            All spaces
            <LuArrowRight
              aria-hidden="true"
              className="size-3.5 transition-transform duration-(--duration-fast) ease-standard group-hover:translate-x-0.5 motion-reduce:transition-none"
            />
          </Link>
        </div>

        {others.data && (
          <MediaCardRow className="mt-block">
            {others.data.map((other) => (
              <SpaceCard key={other.id} space={other} />
            ))}
          </MediaCardRow>
        )}
      </section>
    </>
  );
}
